// Pure nutrition math for Grit. No Supabase, no DOM (so it is unit-tested).
//
// Maintenance model (chosen to avoid double-counting): BMR x a fixed sedentary
// baseline (1.2) for daily living, PLUS the MEASURED workout burn from the logged
// sessions/runs. We never expose an adjustable activity multiplier, because that
// number already bakes in exercise and would double-count the workouts Grit logs.

const LB_TO_KG = 0.45359237;
const IN_TO_CM = 2.54;
const BASELINE_FACTOR = 1.2;   // sedentary NEAT; workouts added separately as measured burn
const LIFT_MET = 4.5;          // resistance training, netted down for long rest periods (Compendium 3.5-6.0)
const HIIT_MET = 8.0;          // vigorous circuit / HIIT
const HIIT_DEFAULT_MIN = 25;   // HIIT logs as a checkbox (no minutes), so assume a typical session

// Running METs by speed (2024 Compendium). Pick the highest band the pace reaches.
const RUN_MET_BY_MPH = [[8.0, 12.0], [7.5, 11.8], [7.0, 11.0], [6.7, 10.5], [6.0, 9.3], [5.2, 9.0], [5.0, 8.5], [0, 7.0]];
function runMet(mph) { for (const [min, met] of RUN_MET_BY_MPH) if (mph >= min) return met; return 7.0; }

export function ftInToInches(ft, inch) { return (Number(ft) || 0) * 12 + (Number(inch) || 0); }
export function inchesToFtIn(inches) { const n = Number(inches) || 0; return { ft: Math.floor(n / 12), in: Math.round(n % 12) }; }

// Mifflin-St Jeor. Returns null when inputs are missing so the UI can prompt.
export function bmr({ weightLb, heightIn, age, sex }) {
  if (!weightLb || !heightIn || age == null || age === '') return null;
  const kg = weightLb * LB_TO_KG;
  const cm = heightIn * IN_TO_CM;
  const base = 10 * kg + 6.25 * cm - 5 * Number(age);
  return Math.round(base + (sex === 'female' ? -161 : 5));
}

const metBurn = (met, weightLb, minutes) => Math.round(met * weightLb * LB_TO_KG * (minutes / 60));

export function liftBurn(durationMin, weightLb) {
  if (!durationMin || !weightLb) return 0;
  return metBurn(LIFT_MET, weightLb, durationMin);
}
export function runBurn({ actualMinutes, actualDistanceMi, treadmillMph, weightLb }) {
  if (!weightLb || !actualMinutes) return 0;
  let met = 9.3; // ~10:00/mi default if we somehow have no pace
  if (actualDistanceMi && actualMinutes) met = runMet(actualDistanceMi / (actualMinutes / 60));
  else if (treadmillMph) met = runMet(Number(treadmillMph));
  return metBurn(met, weightLb, actualMinutes);
}
export function hiitBurn(weightLb, minutes) {
  if (!weightLb) return 0;
  return metBurn(HIIT_MET, weightLb, minutes || HIIT_DEFAULT_MIN);
}

// Total estimated burn for a date from logged lifts + runs (runs include hiit).
export function workoutBurnForDate(dateISO, sessions = [], runs = [], weightLb) {
  if (!weightLb) return 0;
  let total = 0;
  for (const s of sessions) if (s.date === dateISO) total += liftBurn(s.duration_min, weightLb);
  for (const r of runs) {
    if (r.date !== dateISO) continue;
    total += r.type === 'hiit'
      ? hiitBurn(weightLb, r.actual_minutes)
      : runBurn({ actualMinutes: r.actual_minutes, actualDistanceMi: r.actual_distance_mi, treadmillMph: r.treadmill_mph, weightLb });
  }
  return Math.round(total);
}

export function macrosToKcal(p, c, f) { return Math.round((Number(p) || 0) * 4 + (Number(c) || 0) * 4 + (Number(f) || 0) * 9); }

// The day's energy picture. deficit = maintenance - intake (positive = under = good on a cut).
export function dayEnergy({ intakeKcal, bmrVal, workoutBurnKcal = 0 }) {
  if (bmrVal == null) return { ready: false };
  const baseline = Math.round(bmrVal * BASELINE_FACTOR);
  const workoutBurn = Math.round(workoutBurnKcal || 0);
  const maintenance = baseline + workoutBurn;
  const intake = Math.round(intakeKcal || 0);
  const deficit = maintenance - intake;
  const state = deficit > 50 ? 'under' : deficit < -50 ? 'over' : 'even';
  const label = state === 'under' ? `${deficit} under` : state === 'over' ? `${Math.abs(deficit)} over` : 'about even';
  return { ready: true, baseline, workoutBurn, maintenance, intake, deficit, state, label };
}

// ── Goal-based calorie budget ────────────────────────────────────────────────
export const CAL_PER_LB = 3500;     // ~3500 kcal per pound of fat
export const EARN_BACK_PCT = 0.20;  // only 20% of workout burn returns to the day
export const MIN_TARGET_KCAL = 1200; // safety floor: an aggressive rate never drives the target below this

// Daily calorie deficit needed for a target weekly loss. 1 lb/wk = 500 cal/day.
export function dailyDeficitForRate(lbPerWeek) {
  return Math.round(((Number(lbPerWeek) || 0) * CAL_PER_LB) / 7);
}

// The goal model: target = sedentary maintenance minus the planned deficit.
// You can eat back only 20% of the measured workout burn (a manual override wins
// when provided). left = budget - intake = what you can still eat today.
export function dayBudget({ bmrVal, lossRateLbPerWk, workoutBurnKcal = 0, burnOverride = null, intakeKcal = 0 }) {
  if (bmrVal == null || !lossRateLbPerWk) return { ready: false };
  const baseMaintenance = Math.round(bmrVal * BASELINE_FACTOR);
  const rawTarget = baseMaintenance - dailyDeficitForRate(lossRateLbPerWk);
  const target = Math.max(MIN_TARGET_KCAL, rawTarget);
  const floored = rawTarget < MIN_TARGET_KCAL;
  const hasOverride = burnOverride != null && burnOverride !== '' && Number.isFinite(Number(burnOverride));
  const burn = Math.round((hasOverride ? Number(burnOverride) : workoutBurnKcal) || 0);
  const earnedBack = Math.round(burn * EARN_BACK_PCT);
  const budget = target + earnedBack;
  const intake = Math.round(intakeKcal || 0);
  const left = budget - intake;
  return {
    ready: true, baseMaintenance, target, floored, burn, earnedBack, budget, intake, left,
    overridden: hasOverride,
    state: left < -50 ? 'over' : left > 50 ? 'under' : 'even',
  };
}

// ── Weight timeline + projections ────────────────────────────────────────────
export function daysBetween(a, b) {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000);
}
export function addDays(iso, n) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

// Build the weight chart series:
//  - actual: logged weigh-ins (solid line)
//  - pace: straight goal-pace line from the first weigh-in at lossRate, clamped at goal
//  - expected: where weight should be from the real daily deficits actually eaten
//    (only days with food logged count; carry the last known weight forward for BMR)
export function weightProjection({
  weights = [], heightIn, age, sex, goalWeightLb = null, lossRateLbPerWk = null,
  foodTotals = {}, sessions = [], runs = [], today,
}) {
  const sorted = weights
    .map((w) => ({ date: w.date, w: Number(w.weight_lb) }))
    .filter((w) => w.date && Number.isFinite(w.w))
    .sort((a, b) => (a.date < b.date ? -1 : 1));
  if (!sorted.length) return { ready: false };

  const start = sorted[0];
  const lastLog = sorted[sorted.length - 1].date;
  const end = today && today > lastLog ? today : lastLog;
  const weightOn = (date) => { let w = start.w; for (const p of sorted) { if (p.date <= date) w = p.w; else break; } return w; };

  const actual = sorted.filter((p) => p.date <= end).map((p) => ({ x: daysBetween(start.date, p.date), y: p.w, date: p.date }));

  // expected line from real deficits (history only, start..end)
  const expected = [];
  let cum = 0;
  const histDays = Math.max(0, daysBetween(start.date, end));
  for (let i = 0; i <= histDays; i++) {
    const d = addDays(start.date, i);
    // This morning's expected weight reflects deficits from PRIOR days only, so
    // day 0 sits exactly on the start weight (shared origin with actual + pace).
    expected.push({ x: i, y: Math.round((start.w - cum / CAL_PER_LB) * 10) / 10, date: d });
    const intake = Number(foodTotals[d] || 0);
    if (intake > 0) {
      const wOn = weightOn(d);
      const base = bmr({ weightLb: wOn, heightIn, age, sex });
      if (base != null) {
        const maint = Math.round(base * BASELINE_FACTOR) + workoutBurnForDate(d, sessions, runs, wOn);
        cum += maint - intake;
      }
    }
  }

  // goal-pace line, extended to the projected goal date so the whole path shows
  let projGoalDate = null;
  if (goalWeightLb != null && lossRateLbPerWk > 0 && start.w > goalWeightLb) {
    projGoalDate = addDays(start.date, Math.round(((start.w - goalWeightLb) / lossRateLbPerWk) * 7));
  }
  const paceEnd = projGoalDate && projGoalDate > end ? projGoalDate : end;
  const paceDays = Math.max(histDays, daysBetween(start.date, paceEnd));
  const pace = [];
  if (lossRateLbPerWk > 0) {
    for (let i = 0; i <= paceDays; i++) {
      let y = start.w - lossRateLbPerWk * (i / 7);
      if (goalWeightLb != null) y = Math.max(goalWeightLb, y);
      pace.push({ x: i, y: Math.round(y * 10) / 10, date: addDays(start.date, i) });
    }
  }

  const ys = [...actual.map((p) => p.y), ...expected.map((p) => p.y), ...pace.map((p) => p.y)];
  if (goalWeightLb != null) ys.push(goalWeightLb);
  const totalDays = paceDays;
  const todayX = today ? daysBetween(start.date, today) : histDays;
  return {
    ready: actual.length >= 1,
    startDate: start.date, endDate: end, totalDays, todayX,
    actual, expected, pace, goalWeightLb, projGoalDate,
    yMin: Math.min(...ys), yMax: Math.max(...ys),
  };
}

// ── Bigger Leaner Stronger calories (4th ed., ch.9-10) ───────────────────────
// One number a day from body weight: weight x calories-per-pound for the goal and
// how many hours a week you train (chart p.161; p.137-141). No resting-burn formula
// and no eating back workouts: the multiplier already includes your training
// (p.118, 138, 391), and burn estimates are unreliable (p.97, 340).
export const BOOK_CAL_PER_LB = {
  cut:      { sedentary: 8,  light: 10, moderate: 12, high: 14 },
  gain:     { sedentary: 16, light: 16, moderate: 18, high: 20 },
  maintain: { sedentary: 12, light: 14, moderate: 16, high: 18 },
};
export const BOOK_ACTIVITY = [
  ['light', 'Light', '1-4 h a week'],
  ['moderate', 'Moderate', '5-8 h a week'],
  ['high', 'High', '9+ h a week'],
];
export const BOOK_GOALS = [['cut', 'Cut'], ['gain', 'Lean gain'], ['maintain', 'Maintain']];
// Protein per pound: cut 1-1.2, lean gain and maintain 0.8-1 (p.141). Fat 20-30% (p.144).
export const BOOK_PROTEIN_G_PER_LB = { cut: 1.0, gain: 0.9, maintain: 0.9 };
export const BOOK_FAT_PCT = 0.25;
export const DIET_BREAK_CAL_PER_LB = 14; // maintenance for a 5-7 day diet break (p.152-153)
export const DIET_BREAK_EVERY_WEEKS = 8;  // on a cut, a break every 6-8 weeks
export const DIET_BREAK_DAYS = 7;

export function bookPlan({ weightLb, goal = 'cut', activity = 'moderate', dietBreak = false }) {
  const w = Number(weightLb);
  if (!w || w <= 0) return { ready: false };
  const g = BOOK_CAL_PER_LB[goal] ? goal : 'cut';
  const a = BOOK_CAL_PER_LB[g][activity] != null ? activity : 'moderate';
  const perLb = dietBreak && g === 'cut' ? DIET_BREAK_CAL_PER_LB : BOOK_CAL_PER_LB[g][a];
  const calories = Math.round((w * perLb) / 10) * 10;
  const proteinG = Math.round(w * BOOK_PROTEIN_G_PER_LB[g]);
  // On a diet break the extra calories come mostly from carbs (p.153): keep the cut's fat.
  const fatBase = dietBreak && g === 'cut' ? Math.round(w * BOOK_CAL_PER_LB.cut[a]) : calories;
  const fatG = Math.round((fatBase * BOOK_FAT_PCT) / 9);
  const carbsG = Math.max(0, Math.round((calories - proteinG * 4 - fatG * 9) / 4));
  return { ready: true, goal: g, activity: a, perLb, weightLb: w, calories, proteinG, carbsG, fatG, dietBreak: dietBreak && g === 'cut' };
}

// Is a diet break due? Weeks on the cut since it started or since the last break ended.
export function dietBreakDue({ goal, cutStartISO, lastBreakEndISO, todayISO }) {
  if (goal !== 'cut' || !cutStartISO || !todayISO) return { due: false, weeks: 0 };
  const from = lastBreakEndISO && lastBreakEndISO > cutStartISO ? lastBreakEndISO : cutStartISO;
  const weeks = Math.floor(daysBetween(from, todayISO) / 7);
  return { due: weeks >= DIET_BREAK_EVERY_WEEKS, weeks };
}

// Judge weight by 2-week averages, not daily swings (p.341-342). Blocks are 14 days
// counted BACK from endISO (today), so the newest block is always "the last 14 days"
// and the one before it is the 14 days before that. Returns oldest first.
export function twoWeekAverages(weights = [], endISO = null) {
  const pts = weights
    .map((w) => ({ date: w.date, w: Number(w.weight_lb) }))
    .filter((p) => p.date && w_ok(p.w))
    .sort((a, b) => (a.date < b.date ? -1 : 1));
  if (!pts.length) return [];
  const end = endISO && endISO > pts[pts.length - 1].date ? endISO : pts[pts.length - 1].date;
  const buckets = new Map();
  for (const p of pts) {
    const k = Math.floor(daysBetween(p.date, end) / 14);
    if (!buckets.has(k)) buckets.set(k, []);
    buckets.get(k).push(p.w);
  }
  return [...buckets.entries()].sort((a, b) => b[0] - a[0]).map(([k, ws]) => ({
    period: k, startDate: addDays(end, -(k * 14 + 13)), endDate: addDays(end, -k * 14),
    avg: Math.round((ws.reduce((s, x) => s + x, 0) / ws.length) * 10) / 10, count: ws.length,
  }));
}
function w_ok(n) { return Number.isFinite(n) && n > 0; }
